import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const QCChecksList = forwardRef(({ onSelectCheck, selectedCheckId, onNew }, ref) => {
  const { t } = useTranslation('qc');
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchChecks
  }));

  useEffect(() => {
    fetchChecks();
  }, [statusFilter, typeFilter]);

  const fetchChecks = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/qc/checks?limit=200');
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (typeFilter !== 'all') {
        url += `&check_type=${typeFilter}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setChecks(data);
      } else {
        setError(t('checks.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching checks:', error);
      setError(t('checks.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredChecks = checks.filter(check => {
    const matchesSearch = (
      (check.checker_name && check.checker_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (check.check_type && check.check_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (check.notes && check.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (check.defects_found && check.defects_found.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    return matchesSearch;
  });

  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING': 'status-warning',
      'PASSED': 'status-active',
      'FAILED': 'status-error',
      'CONDITIONAL': 'status-warning'
    };
    return (
      <span className={`status-badge ${statusMap[status] || 'status-inactive'}`}>
        {t(`checks.status.${status.toLowerCase()}`, status)}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    return <span className="dept-code">{t(`checks.types.${type.toLowerCase()}`, type)}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatAppDate(dateStr);
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && checks.length === 0) {
    return <div className="list-container"><div className="loading">{t('checks.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('checks.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('checks.newCheck')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('checks.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('checks.filters.allStatus')}</option>
            <option value="PENDING">{t('checks.status.pending')}</option>
            <option value="PASSED">{t('checks.status.passed')}</option>
            <option value="FAILED">{t('checks.status.failed')}</option>
            <option value="CONDITIONAL">{t('checks.status.conditional')}</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('checks.filters.allTypes')}</option>
            <option value="INCOMING">{t('checks.types.incoming')}</option>
            <option value="IN_PROCESS">{t('checks.types.in_process')}</option>
            <option value="FINAL">{t('checks.types.final')}</option>
            <option value="BATCH">{t('checks.types.batch')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('checks.tableHeaders.id')}</th>
              <th>{t('checks.tableHeaders.type')}</th>
              <th>{t('checks.tableHeaders.date')}</th>
              <th>{t('checks.tableHeaders.checkedBy')}</th>
              <th>{t('checks.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredChecks.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('checks.noData.searching') : t('checks.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredChecks.map(check => (
                <tr
                  key={check.check_id}
                  onClick={() => onSelectCheck(check.check_id)}
                  className={selectedCheckId === check.check_id ? 'selected' : ''}
                >
                  <td>
                    <span className="dept-code">#{check.check_id}</span>
                  </td>
                  <td>
                    {getTypeBadge(check.check_type)}
                  </td>
                  <td>
                    {formatDate(check.check_date)}
                  </td>
                  <td>
                    <strong>{check.checker_name || '-'}</strong>
                  </td>
                  <td>
                    {getStatusBadge(check.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default QCChecksList;
